import DOMPurify from "dompurify"

const DECLARATIVE_MUTATION_TAGS = [
  "animate",
  "animatecolor",
  "animatemotion",
  "animatetransform",
  "set",
]
const REFERENCE_ATTRIBUTES = new Set(["href", "xlink:href", "src"])
const PNG_REFERENCE_ELEMENTS = new Set(["image", "feimage"])
const CSS_RESOURCE_ATTRIBUTES = new Set([
  "clip-path",
  "color-profile",
  "cursor",
  "fill",
  "filter",
  "marker",
  "marker-end",
  "marker-mid",
  "marker-start",
  "mask",
  "stroke",
  "style",
])
const INTERNAL_CSS_URL_RE = /url\(\s*(?:#[^"'()\\\s]+|"#[^"()\\\s]+"|'#[^'()\\\s]+')\s*\)/giu
const CSS_URL_START_RE = /url\s*\(/iu
const CSS_IMAGE_SET_FN_RE = /(?:^|[^\w-])(?:-webkit-)?image-set\s*\(/iu
const CSS_OBFUSCATION_RE = /\\|\/\*|@import/iu
const BASE64_RE = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/
const PNG_DATA_URL_RE = /^data:image\/png;base64,([A-Za-z0-9+/=]+)$/
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]

function readUint32(binary: string, offset: number): number {
  return (
    ((binary.charCodeAt(offset) << 24) >>> 0) +
    (binary.charCodeAt(offset + 1) << 16) +
    (binary.charCodeAt(offset + 2) << 8) +
    binary.charCodeAt(offset + 3)
  )
}

function hasStrictPngStructure(binary: string): boolean {
  if (
    binary.length < 45 ||
    PNG_SIGNATURE.some((byte, index) => binary.charCodeAt(index) !== byte)
  ) {
    return false
  }

  let offset = PNG_SIGNATURE.length
  let sawHeader = false
  let sawImageData = false

  while (offset + 12 <= binary.length) {
    const dataLength = readUint32(binary, offset)
    const chunkType = binary.slice(offset + 4, offset + 8)
    const nextOffset = offset + 12 + dataLength

    if (!/^[A-Za-z]{4}$/.test(chunkType) || nextOffset > binary.length) return false
    if (!sawHeader) {
      if (chunkType !== "IHDR" || dataLength !== 13) return false
      const width = readUint32(binary, offset + 8)
      const height = readUint32(binary, offset + 12)
      if (width === 0 || height === 0) return false
      sawHeader = true
    } else if (chunkType === "IHDR") {
      return false
    }

    if (chunkType === "IDAT") sawImageData = true
    if (chunkType === "IEND") {
      return dataLength === 0 && sawImageData && nextOffset === binary.length
    }
    offset = nextOffset
  }

  return false
}

function isStrictPngDataUrl(value: string): boolean {
  const match = PNG_DATA_URL_RE.exec(value)
  const encoded = match?.[1]
  if (!encoded || encoded.length % 4 !== 0 || !BASE64_RE.test(encoded)) return false

  try {
    const binary = atob(encoded)
    return btoa(binary) === encoded && hasStrictPngStructure(binary)
  } catch {
    return false
  }
}

function isAllowedReference(element: Element, value: string): boolean {
  const reference = value.trim()
  if (/^#[^"'()\\\s]+$/u.test(reference)) return true

  const elementName = element.localName.toLowerCase()
  return PNG_REFERENCE_ELEMENTS.has(elementName) && isStrictPngDataUrl(reference)
}

function hasUnsafeCssResource(attributeName: string, value: string): boolean {
  const withoutInternalUrls = value.replace(INTERNAL_CSS_URL_RE, "")
  if (CSS_URL_START_RE.test(withoutInternalUrls)) return true

  if (!CSS_RESOURCE_ATTRIBUTES.has(attributeName)) return false
  if (CSS_IMAGE_SET_FN_RE.test(value)) return true
  if (CSS_OBFUSCATION_RE.test(value)) return true
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0
    return codePoint <= 0x1f || codePoint === 0x7f
  })
}

/**
 * Sanitize untrusted SVG for insertion into the parent document.
 *
 * DOMPurify removes active markup. The local post-pass closes SVG's remaining
 * network-capable reference paths without changing the stored artifact source.
 */
export function sanitizeInlineSvg(source: string): string {
  const sanitized = DOMPurify.sanitize(source, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: ["style", ...DECLARATIVE_MUTATION_TAGS],
    FORBID_ATTR: ["xml:base"],
    RETURN_DOM_FRAGMENT: true,
  })

  for (const element of sanitized.querySelectorAll("*")) {
    for (const attribute of Array.from(element.attributes)) {
      const attributeName = attribute.name.toLowerCase()
      const attributeValue = attribute.value

      if (
        attributeName === "xml:base" ||
        (REFERENCE_ATTRIBUTES.has(attributeName) && !isAllowedReference(element, attributeValue)) ||
        hasUnsafeCssResource(attributeName, attributeValue)
      ) {
        element.removeAttribute(attribute.name)
      }
    }
  }

  const template = document.createElement("template")
  template.content.append(sanitized)
  return template.innerHTML
}
